import { useMemo } from "react";
import { Panel } from "rsuite";
import { useTranslation } from "react-i18next";
import PieChart from "./PieChart";
import Spinner, { spinnerTypes } from "./Spinner";
import MessageBox from "./MessageBox";
import dev from "../models/joinDevice.json";
import "./DeviceTypeChartPanel.css";

const DeviceTypeChartPanel = ({ devices = [], loading = false }) => {
  const [t] = useTranslation();

  const { byType, typeLegend, byState, stateLegend, byBattery, batteryLegend } =
    useMemo(() => {
      const allTypes = Object.values(dev.types);
      const typeCounter = allTypes
        .map((type) => ({
          name: type,
          value: devices.filter((d) => d.type === type).length,
        }))
        .filter((x) => x.value > 0);

      const onlineCount = devices.filter(
        (d) => d.state === dev.typestates.on
      ).length;
      const statePie = [
        { name: t("device-stats.online"), value: onlineCount },
        { name: t("device-stats.offline"), value: devices.length - onlineCount },
      ].filter((s) => s.value > 0);

      const healthyLabel = "70-100%";
      const warnLabel = "26-70%";
      const critLabel = "0-25%";
      let healthy = 0,
        warn = 0,
        crit = 0;
      devices.forEach((d) => {
        const b = parseInt(d.battery);
        if (isNaN(b)) return;
        if (b > 70) healthy += 1;
        else if (b > 25) warn += 1;
        else crit += 1;
      });
      const batteryPie = [
        { name: healthyLabel, value: healthy },
        { name: warnLabel, value: warn },
        { name: critLabel, value: crit },
      ].filter((s) => s.value > 0);

      return {
        byType: typeCounter,
        typeLegend: typeCounter.map((x) => x.name),
        byState: statePie,
        stateLegend: statePie.map((x) => x.name),
        byBattery: batteryPie,
        batteryLegend: batteryPie.map((x) => x.name),
      };
    }, [devices, t]);

  const hasData = devices.length > 0;

  return (
    <Panel className="content">
      {hasData && (
        <div className="device-charts">
          <div className="device-chart">
            <span className="device-chart__caption">
              {t("device-type-chart-panel.devices-by-type")}
            </span>
            <PieChart
              modern
              centerLabel={t("device-type-chart-panel.devices")}
              data={byType}
              legendData={typeLegend}
              nameItems={t("device-type-chart-panel.device")}
              style={{ height: 320 }}
            />
          </div>
          <div className="device-chart">
            <span className="device-chart__caption">
              {t("device-stats.online")} / {t("device-stats.offline")}
            </span>
            <PieChart
              modern
              labelInside
              centerLabel={t("device-type-chart-panel.devices")}
              data={byState}
              legendData={stateLegend}
              nameItems={t("device-type-chart-panel.device")}
              colors={["#2bbf7a", "#fe708c"]}
              style={{ height: 320 }}
            />
          </div>
          <div className="device-chart">
            <span className="device-chart__caption">
              {t("home.avg-battery")}
            </span>
            <PieChart
              modern
              labelInside
              centerLabel={t("device-type-chart-panel.devices")}
              data={byBattery}
              legendData={batteryLegend}
              nameItems={t("device-type-chart-panel.device")}
              colors={["#2bbf7a", "#ffb300", "#fe708c"]}
              style={{ height: 320 }}
            />
          </div>
        </div>
      )}
      {!hasData && !loading && (
        <MessageBox message={t("_common.empty-message")} />
      )}
      {loading && (
        <Spinner
          spinnerType={spinnerTypes.RotatingSquare}
          backdrop={false}
          center={false}
          className="size-charts"
        />
      )}
    </Panel>
  );
};

export default DeviceTypeChartPanel;
